import { useState } from 'react';
import { Loader2, Plus, Save, Trash2 } from 'lucide-react';
import {
  apiService,
  type PanelGameGroupTargetDTO,
  type PanelGameGroupTargetInput,
  type PanelProjectDTO,
  type ProjectMetaDTO,
} from '@/services/api.service';
import { useGameGroupsStore } from '@/stores/gameGroups.store';
import { normalizeGameGroupKey } from '@/utils/gameGroups';
import { databaseCell, sharedVerdict, verdictSuffix } from '@/utils/sharedDatabases';
import admin from '@/pages/AdminPage.module.scss';
import styles from './ProjectGameGroups.module.scss';

/**
 * Groupes de jeu d'un projet (NationsGlory : Nova, Iris...), dans Paramètres > Projets.
 *
 * - Racine : le groupe servi par le satellite déclaré sur la ligne du projet (URL, kid, clé).
 * - Jeton CICD : le panel y lit les groupes du projet (clés, libellés, couleurs). En écriture seule.
 * - Cibles : le satellite de chaque autre groupe (URL, kid, clé propres). Une cible peut avoir la
 *   même URL que la racine : c'est alors le satellite d'entrée qui relaie le groupe
 *   (relay.groups.<clé> de son config.yaml). Le kid et la clé restent propres au groupe.
 *
 * Aucun secret n'est jamais relu : l'API ne renvoie que hasSigningKey et hasCicdToken.
 */

const DEFAULT_TIMEOUT_MS = 20000;

interface TargetDraft {
  key: string;
  baseUrl: string;
  signingKeyId: string;
  /** Vide = clé existante conservée. */
  signingKey: string;
  enabled: boolean;
  timeoutMs: number;
  hasSigningKey: boolean;
  isNew: boolean;
  /** Seules les cibles modifiées partent au serveur : une cible gardée n'est pas revalidée. */
  dirty: boolean;
}

interface GroupsDraft {
  root: string;
  /** Vide = jeton existant conservé. */
  cicdToken: string;
  targets: TargetDraft[];
}

function timeAgo(ts?: number): string {
  if (!ts) return 'jamais';
  const s = Math.max(0, (Date.now() - ts) / 1000);
  if (s < 60) return "à l'instant";
  const m = Math.floor(s / 60); if (m < 60) return `il y a ${m} min`;
  const h = Math.floor(m / 60); if (h < 24) return `il y a ${h} h`;
  return `il y a ${Math.floor(h / 24)} j`;
}

function sameUrl(a: string | null | undefined, b: string | null | undefined): boolean {
  const norm = (u: string | null | undefined) => (u ?? '').trim().replace(/\/+$/, '').toLowerCase();
  return !!norm(a) && norm(a) === norm(b);
}

function draftFrom(project: PanelProjectDTO): GroupsDraft {
  const gg = project.gameGroups;
  const targets = Object.entries(gg?.targets ?? {}).map(([key, t]): TargetDraft => ({
    key,
    baseUrl: t.baseUrl ?? '',
    signingKeyId: t.signingKeyId ?? '',
    signingKey: '',
    enabled: t.enabled,
    timeoutMs: t.timeoutMs || DEFAULT_TIMEOUT_MS,
    hasSigningKey: t.hasSigningKey,
    isNew: false,
    dirty: false,
  }));
  return { root: gg?.root ?? project.meta?.group ?? '', cicdToken: '', targets };
}

function HealthChip({ meta }: { meta: ProjectMetaDTO | null | undefined }) {
  const health = !meta ? 'unknown' : meta.healthy ? 'up' : 'down';
  const cls = health === 'up' ? admin.healthUp : health === 'down' ? admin.healthDown : admin.healthUnknown;
  const label = health === 'up' ? 'Joignable' : health === 'down' ? 'Injoignable' : 'Jamais testé';
  return <span className={`${admin.health} ${cls}`}>{label}</span>;
}

/**
 * Bases de la racine et de la cible, côte à côte, avec leur serveur (satellites 1.0.19 ou plus) : une
 * base propre au groupe partagée (même serveur et même nom) saute aux yeux.
 */
function DatabasesTable({ rootLabel, label, root, target, rootServers, targetServers }: {
  rootLabel: string;
  label: string;
  root?: Record<string, string>;
  target?: Record<string, string>;
  rootServers?: Record<string, string>;
  targetServers?: Record<string, string>;
}) {
  const keys = Array.from(new Set([...Object.keys(root ?? {}), ...Object.keys(target ?? {})])).sort();
  if (keys.length === 0) return null;
  return (
    <details className={styles.databases}>
      <summary>Bases utilisées</summary>
      <table className={styles.dbTable}>
        <thead>
          <tr><th>Base</th><th>{rootLabel}</th><th>{label}</th></tr>
        </thead>
        <tbody>
          {keys.map((k) => {
            const a = root?.[k];
            const b = target?.[k];
            const verdict = sharedVerdict(k, a, b, rootServers?.[k], targetServers?.[k]);
            return (
              <tr key={k} className={verdict ? styles.dbShared : undefined}>
                <td>{k}</td>
                <td>{databaseCell(a, rootServers?.[k])}</td>
                <td>{databaseCell(b, targetServers?.[k])}{verdictSuffix(verdict)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </details>
  );
}

export function ProjectGameGroups({ project, onChanged }: {
  project: PanelProjectDTO;
  /** Recharge la liste des projets après une écriture. */
  onChanged: () => void;
}) {
  const [draft, setDraft] = useState<GroupsDraft | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<{ ok: boolean; message: string } | null>(null);
  const refreshGroups = useGameGroupsStore((s) => s.fetch);

  const gg = project.gameGroups;
  const catalogGroups = gg?.catalog?.groups ?? [];
  const labelOf = (key: string | null | undefined) =>
    (key && catalogGroups.find((g) => g.key === key)?.label) || key || '?';
  const colorOf = (key: string) => catalogGroups.find((g) => g.key === key)?.color;
  const rootLabel = labelOf(gg?.root);

  const afterWrite = (message: string) => {
    setFeedback({ ok: true, message });
    onChanged();
    // Le sélecteur Nova | Iris suit tout de suite, sans attendre un rechargement.
    void refreshGroups({ force: true });
  };

  const save = async () => {
    if (!draft) return;
    const root = normalizeGameGroupKey(draft.root);
    if (!root) {
      setFeedback({ ok: false, message: 'Racine invalide : clé du groupe servi par le satellite actuel (ex. « nova »).' });
      return;
    }
    const targets: Record<string, PanelGameGroupTargetInput> = {};
    for (const t of draft.targets) {
      if (!t.dirty && !t.isNew) continue;
      const key = normalizeGameGroupKey(t.key);
      if (!key) { setFeedback({ ok: false, message: `Clé de groupe invalide : « ${t.key} ».` }); return; }
      if (key === root) { setFeedback({ ok: false, message: `La cible « ${key} » est la racine : son satellite est celui du projet.` }); return; }
      if (!t.baseUrl.trim()) { setFeedback({ ok: false, message: `Cible ${labelOf(key)} : URL obligatoire.` }); return; }
      if (!t.signingKeyId.trim()) { setFeedback({ ok: false, message: `Cible ${labelOf(key)} : identifiant de clé (kid) obligatoire.` }); return; }
      if (t.isNew && !t.signingKey.trim() && !t.hasSigningKey) {
        setFeedback({ ok: false, message: `Cible ${labelOf(key)} : clé de signature obligatoire.` });
        return;
      }
      targets[key] = {
        baseUrl: t.baseUrl.trim(),
        signingKeyId: t.signingKeyId.trim(),
        // Omise quand vide : le panel garde alors la clé existante.
        signingKey: t.signingKey.trim() || undefined,
        enabled: t.enabled,
        timeoutMs: t.timeoutMs,
      };
    }
    setBusy('save');
    const res = await apiService.savePanelProjectGameGroups(project.id, {
      root,
      cicdToken: draft.cicdToken.trim() || undefined,
      targets,
    });
    setBusy(null);
    if (res.error) {
      setFeedback({ ok: false, message: res.error });
      return;
    }
    setDraft(null);
    afterWrite('Groupes enregistrés, satellites sondés : leur état est à jour ci-dessous.');
  };

  const removeTarget = async (key: string) => {
    const label = labelOf(key);
    if (!window.confirm(`Retirer la cible ${label} ? Le sélecteur ne proposera plus ${label} ; les données de ses bases restent.`)) return;
    setBusy(`target:${key}`);
    const res = await apiService.deletePanelProjectGameGroupTarget(project.id, key);
    setBusy(null);
    if (res.error) { setFeedback({ ok: false, message: res.error }); return; }
    afterWrite(`Cible ${label} retirée.`);
  };

  const removeAll = async () => {
    if (!window.confirm(`Retirer les groupes de jeu de ${project.name} ? Le panel reviendra au satellite unique, sans groupe.`)) return;
    setBusy('all');
    const res = await apiService.deletePanelProjectGameGroups(project.id);
    setBusy(null);
    // 409 : le satellite racine annonce encore un groupe (message explicite du panel).
    if (res.error) { setFeedback({ ok: false, message: res.error }); return; }
    afterWrite('Groupes de jeu retirés : le projet est servi par son satellite unique.');
  };

  // ---- Éditeur ----
  if (draft) {
    const setField = <K extends keyof GroupsDraft>(k: K, v: GroupsDraft[K]) => setDraft({ ...draft, [k]: v });
    const setTarget = (i: number, patch: Partial<TargetDraft>) => setDraft({
      ...draft,
      targets: draft.targets.map((t, j) => (j === i ? { ...t, ...patch, dirty: true } : t)),
    });
    const rootKey = normalizeGameGroupKey(draft.root);
    const taken = new Set(draft.targets.map((t) => normalizeGameGroupKey(t.key)));
    const addable = catalogGroups.filter((g) => g.key !== rootKey && !taken.has(g.key));
    const addTarget = (key: string) => setDraft({
      ...draft,
      targets: [...draft.targets, {
        key, baseUrl: '', signingKeyId: '', signingKey: '', enabled: true,
        timeoutMs: DEFAULT_TIMEOUT_MS, hasSigningKey: false, isNew: true, dirty: true,
      }],
    });

    return (
      <div className={styles.section}>
        <div className={styles.sectionTitle}>Groupes de jeu</div>
        <div className={admin.gradeEditor}>
          <div className={admin.fieldRow}>
            <div className={admin.field}>
              <label>Racine (groupe du satellite ci-dessus)</label>
              {catalogGroups.length > 0 ? (
                <select className={admin.select} value={draft.root} onChange={(e) => setField('root', e.target.value)}>
                  {!catalogGroups.some((g) => g.key === draft.root) && <option value={draft.root}>{draft.root || '(choisir)'}</option>}
                  {catalogGroups.map((g) => <option key={g.key} value={g.key}>{g.label} ({g.key})</option>)}
                </select>
              ) : (
                <input className={admin.input} value={draft.root} placeholder="nova"
                       onChange={(e) => setField('root', e.target.value)} />
              )}
            </div>
            <div className={admin.field} style={{ flex: 1, minWidth: 240 }}>
              <label>Jeton public du projet dans le CICD</label>
              <input
                className={admin.input}
                type="password"
                autoComplete="new-password"
                value={draft.cicdToken}
                placeholder={gg?.hasCicdToken ? '•••••••• (inchangé)' : 'affiché dans Déploiement'}
                onChange={(e) => setField('cicdToken', e.target.value)}
              />
              <span className={admin.muted} style={{ fontSize: 11 }}>
                Le panel y lit les groupes du projet. Jamais relu par l'API ; vide = jeton actuel conservé.
              </span>
            </div>
          </div>

          {draft.targets.map((t, i) => {
            const label = labelOf(t.key);
            const viaRelay = sameUrl(t.baseUrl, project.baseUrl);
            return (
              <div key={`${t.key}:${i}`} className={styles.targetEditor}>
                <div className={styles.targetHead}>
                  <span className={admin.chipDot} style={{ background: colorOf(t.key) ?? 'var(--color-text-secondary)' }} />
                  <strong>{label}</strong>
                  <code className={admin.muted} style={{ fontSize: 12 }}>{t.key}</code>
                  {t.isNew && <span className={`${admin.health} ${admin.healthUnknown}`}>nouvelle cible</span>}
                </div>
                <div className={admin.field}>
                  <label>URL interne du satellite {label}</label>
                  <div className={styles.inline}>
                    <input className={admin.input} style={{ flex: 1 }} value={t.baseUrl} placeholder="http://172.27.100.1:3031"
                           onChange={(e) => setTarget(i, { baseUrl: e.target.value })} />
                    {project.baseUrl && !viaRelay && (
                      <button type="button" className={admin.iconBtn} onClick={() => setTarget(i, { baseUrl: project.baseUrl ?? '' })}
                              title="Le satellite d'entrée relaie ce groupe sur son réseau interne (aucun port de plus)">
                        Même URL que la racine
                      </button>
                    )}
                  </div>
                  <span className={admin.muted} style={{ fontSize: 11 }}>
                    {viaRelay
                      ? `Même URL que la racine : le satellite d'entrée doit relayer « ${t.key} » (relay.groups.${t.key} dans son config.yaml), et son relay.timeout-ms rester supérieur ou égal au timeout ci-dessous.`
                      : 'Adresse privée uniquement (10.x, 172.16-31.x, 192.168.x, localhost ou *.internal).'}
                  </span>
                </div>
                <div className={admin.fieldRow}>
                  <div className={admin.field}>
                    <label>Identifiant de clé (kid)</label>
                    <input className={admin.input} value={t.signingKeyId} placeholder={`ng-${t.key}-2026-10`}
                           onChange={(e) => setTarget(i, { signingKeyId: e.target.value })} />
                  </div>
                  <div className={admin.field} style={{ flex: 1, minWidth: 220 }}>
                    <label>Clé de signature</label>
                    <input
                      className={admin.input}
                      type="password"
                      autoComplete="new-password"
                      value={t.signingKey}
                      placeholder={t.hasSigningKey ? '•••••••• (inchangée)' : `secret du satellite ${label}`}
                      onChange={(e) => setTarget(i, { signingKey: e.target.value })}
                    />
                    <span className={admin.muted} style={{ fontSize: 11 }}>
                      Propre à {label} : jamais celle de la racine ni d'un autre groupe.
                    </span>
                  </div>
                </div>
                <div className={admin.fieldRow} style={{ marginBottom: 0 }}>
                  <div className={admin.field} style={{ marginBottom: 0 }}>
                    <label>Timeout (ms)</label>
                    <input className={admin.input} type="number" min={1000} step={1000} value={t.timeoutMs} style={{ width: 120 }}
                           onChange={(e) => setTarget(i, { timeoutMs: Number(e.target.value) || DEFAULT_TIMEOUT_MS })} />
                  </div>
                  <label className={admin.adminToggle}>
                    <input type="checkbox" checked={t.enabled} onChange={(e) => setTarget(i, { enabled: e.target.checked })} />
                    Active
                  </label>
                  {t.isNew && (
                    <button type="button" className={admin.iconBtn}
                            onClick={() => setDraft({ ...draft, targets: draft.targets.filter((_, j) => j !== i) })}>
                      Annuler cet ajout
                    </button>
                  )}
                </div>
              </div>
            );
          })}

          <div className={styles.inline} style={{ marginTop: 8 }}>
            {addable.length > 0 ? (
              addable.map((g) => (
                <button key={g.key} type="button" className={admin.iconBtn} onClick={() => addTarget(g.key)}>
                  <Plus size={12} /> Cible {g.label}
                </button>
              ))
            ) : catalogGroups.length === 0 ? (
              <span className={admin.muted} style={{ fontSize: 12 }}>
                Enregistre d'abord la racine et le jeton : les autres groupes viennent du catalogue du CICD.
              </span>
            ) : null}
          </div>

          {feedback && !feedback.ok && <div className={`${admin.probeResult} ${admin.probeKo}`}>{feedback.message}</div>}

          <div className={admin.actions}>
            <button className={admin.saveBtn} onClick={save} disabled={busy === 'save' || !draft.root.trim()}>
              {busy === 'save' ? <Loader2 size={14} className={admin.spin} /> : <Save size={14} />} Enregistrer les groupes
            </button>
            <button className={admin.iconBtn} onClick={() => { setDraft(null); setFeedback(null); }} disabled={busy === 'save'}>
              Annuler
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ---- Lecture ----
  const targets = Object.entries(gg?.targets ?? {}) as [string, PanelGameGroupTargetDTO][];
  const withoutTarget = catalogGroups.filter((g) => g.key !== gg?.root && !gg?.targets[g.key]);
  const announced = project.meta?.group;

  return (
    <div className={styles.section}>
      <div className={styles.sectionHead}>
        <span className={styles.sectionTitle}>Groupes de jeu</span>
        <button className={admin.iconBtn} onClick={() => { setDraft(draftFrom(project)); setFeedback(null); }} disabled={!!busy}>
          {gg ? 'Configurer' : 'Déclarer les groupes'}
        </button>
        {gg && (
          <button className={admin.iconBtn} onClick={removeAll} disabled={!!busy} title="Revenir au satellite unique, sans groupe">
            {busy === 'all' ? <Loader2 size={12} className={admin.spin} /> : <Trash2 size={12} />} Retirer les groupes
          </button>
        )}
      </div>

      {!gg ? (
        <p className={admin.muted} style={{ fontSize: 12 }}>
          Aucun groupe déclaré : {project.name} est servi par son satellite unique, sans sélecteur de groupe.
        </p>
      ) : (
        <>
          <div className={admin.projectMeta}>
            <span>
              racine <strong>{rootLabel}</strong> ({gg.root}) : satellite ci-dessus
              {announced ? `, annonce « ${announced} »` : ', n\'annonce aucun groupe'}
            </span>
            <span>jeton CICD {gg.hasCicdToken ? 'enregistré' : 'manquant'}</span>
            <span>
              catalogue {gg.catalog ? `lu ${timeAgo(gg.catalog.fetchedAt)} (${gg.catalog.groups.length} groupe${gg.catalog.groups.length > 1 ? 's' : ''})` : 'jamais lu'}
            </span>
            {gg.catalog?.error && <span className={admin.probeKo}>CICD : {gg.catalog.error}</span>}
          </div>
          {(project.meta?.warnings ?? []).map((w) => <div key={w} className={styles.warning}>{w}</div>)}
          {gg.catalog && gg.root && !catalogGroups.some((g) => g.key === gg.root) && (
            <div className={styles.warning}>
              {gg.root} n'existe plus dans le CICD : la racine reste servie, mais redéclare le groupe dans le CICD
              ou choisis une autre racine.
            </div>
          )}

          {targets.map(([key, t]) => {
            const label = labelOf(key);
            const viaRelay = sameUrl(t.baseUrl, project.baseUrl);
            const inCatalog = !gg.catalog || catalogGroups.some((g) => g.key === key);
            return (
              <div key={key} className={styles.target}>
                <div className={admin.projectHead}>
                  <span className={admin.chipDot} style={{ background: colorOf(key) ?? 'var(--color-text-secondary)' }} />
                  <span className={admin.projectName}>{label}</span>
                  <code className={admin.muted} style={{ fontSize: 12 }}>{key}</code>
                  <HealthChip meta={t.meta} />
                  {!t.enabled && <span className={`${admin.health} ${admin.healthUnknown}`}>Désactivée</span>}
                  {!t.hasSigningKey && <span className={`${admin.health} ${admin.healthDown}`}>Clé manquante</span>}
                  {!inCatalog && <span className={`${admin.health} ${admin.healthDown}`}>Retiré du CICD</span>}
                  <button className={admin.iconBtn} style={{ marginLeft: 'auto' }} onClick={() => removeTarget(key)} disabled={!!busy}
                          title={`Retirer la cible ${label}`}>
                    {busy === `target:${key}` ? <Loader2 size={12} className={admin.spin} /> : <Trash2 size={12} />}
                  </button>
                </div>
                <div className={admin.projectUrl}>
                  {viaRelay ? `${t.baseUrl} (relayé par le satellite d'entrée)` : (t.baseUrl ?? '(aucune URL)')}
                </div>
                <div className={admin.projectMeta}>
                  <span>kid {t.signingKeyId ?? '?'}</span>
                  <span>timeout {Math.round(t.timeoutMs / 1000)} s</span>
                  {t.meta?.version && <span>version {t.meta.version}</span>}
                  {t.meta?.group && <span>annonce « {t.meta.group} »</span>}
                  {t.meta?.relayed && <span>sondé par le relais</span>}
                  {t.meta?.lastSeen && <span>vu {timeAgo(t.meta.lastSeen)}</span>}
                  {t.meta?.error && <span className={admin.probeKo}>{t.meta.error}</span>}
                </div>
                {(t.meta?.warnings ?? []).map((w) => <div key={w} className={styles.warning}>{w}</div>)}
                <DatabasesTable rootLabel={rootLabel} label={label}
                                root={project.meta?.databases} target={t.meta?.databases ?? undefined}
                                rootServers={project.meta?.databaseServers}
                                targetServers={t.meta?.databaseServers ?? undefined} />
              </div>
            );
          })}

          {withoutTarget.length > 0 && (
            <p className={admin.muted} style={{ fontSize: 12, marginTop: 6 }}>
              Sans satellite déclaré (non proposés dans le sélecteur) : {withoutTarget.map((g) => g.label).join(', ')}.
            </p>
          )}
        </>
      )}

      {feedback && (
        <div className={`${admin.probeResult} ${feedback.ok ? admin.probeOk : admin.probeKo}`}>{feedback.message}</div>
      )}
    </div>
  );
}
